import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { usePageSEO } from "../lib/seo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, CheckCircle, ArrowRight, Shield, BarChart3, FileText, Users, Rocket } from "lucide-react";
import { motion } from "framer-motion";

export default function InvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [step, setStep] = useState("welcome"); // welcome | password | done
  usePageSEO({ title: "You're Invited", noIndex: true });

  useEffect(() => {
    window.scrollTo(0, 0);
    const load = async () => {
      try {
        const { data } = await api.get(`/invite/${token}`);
        setInvite(data);
      } catch {
        setError("This invitation is no longer valid or has already been used.");
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleAccept = async () => {
    if (!password || password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setAccepting(true);
    setError("");
    try {
      const { data } = await api.post(`/invite/${token}/accept`);
      if (data.token) sessionStorage.setItem("auth_token", data.token);
      // Update password for the auto-created account
      // The accept endpoint created a temp password — we need to let them set their own
      // For now, we'll use the magic link flow on their first real login
      // Navigate based on payment status
      if (data.payment_waived) {
        navigate("/blueprint");
      } else {
        navigate("/blueprint");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#0033CC]" /></div>;
  }

  if (!invite) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <img src="/stansbury-logo.png" alt="Stansbury AI" className="h-10 mx-auto mb-6" />
          <h1 className="text-xl font-['Outfit'] font-medium text-[#0F172A] mb-3">Invitation Not Found</h1>
          <p className="text-sm text-slate-500 font-['IBM_Plex_Sans'] mb-6">{error}</p>
          <Button onClick={() => navigate("/")} className="bg-[#0033CC] text-white rounded-none px-8 h-11 font-['Outfit']">Go to Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="invite-page">
      <header className="backdrop-blur-2xl bg-white/80 border-b border-slate-200/60 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex items-center justify-between h-16">
          <img src="/stansbury-logo.png" alt="Stansbury AI" className="h-[46px] sm:h-12 flex-shrink-0" />
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 md:px-12 py-12 md:py-20">
        {step === "welcome" && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="text-center mb-10">
              <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#0033CC] mb-4">Exclusive Invitation</p>
              <h1 className="text-3xl sm:text-4xl font-['Outfit'] font-medium tracking-tight text-[#0F172A] mb-3 leading-tight">
                Welcome, {invite.name}
              </h1>
              <p className="text-base text-slate-500 font-['IBM_Plex_Sans'] max-w-lg mx-auto">
                You've been personally invited to the Stansbury AI <strong className="text-[#0F172A]">{invite.package === "ultimate" ? "Ultimate" : "Growth"} Blueprint</strong> — our most comprehensive AI growth diagnostic.
              </p>
            </div>

            {/* What's Included */}
            <div className="bg-white border border-slate-200 p-8 mb-6">
              <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#0033CC] mb-4">What's Included</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: BarChart3, title: "AI Growth Score", desc: "Scored across 6 business dimensions with industry benchmarks" },
                  { icon: FileText, title: "Executive Report", desc: "7-page branded report with agentic AI recommendations" },
                  { icon: Rocket, title: "Presentation Deck", desc: "12-slide executive deck for your leadership team" },
                  { icon: Users, title: "Strategist Review", desc: "Live walkthrough of findings with a senior advisor" },
                ].map((item) => (
                  <div key={item.title} className="flex gap-3">
                    <div className="w-9 h-9 bg-[#E5EDFF] flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-4 h-4 text-[#0033CC]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-sm font-['Outfit'] font-medium text-[#0F172A]">{item.title}</p>
                      <p className="text-xs text-slate-500 font-['IBM_Plex_Sans']">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Payment Status */}
            {!invite.payment_required ? (
              <div className="bg-emerald-50 border border-emerald-200 p-5 mb-6 flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-['Outfit'] font-medium text-emerald-800">Payment Complete</p>
                  <p className="text-xs text-emerald-600 font-['IBM_Plex_Sans']">Your investment has been taken care of. You can proceed directly to your blueprint.</p>
                </div>
              </div>
            ) : (
              <div className="bg-blue-50 border border-blue-200 p-5 mb-6 flex items-center gap-3">
                <Shield className="w-5 h-5 text-[#0033CC] flex-shrink-0" />
                <div>
                  <p className="text-sm font-['Outfit'] font-medium text-[#0F172A]">Secure Checkout</p>
                  <p className="text-xs text-slate-600 font-['IBM_Plex_Sans']">You'll complete a quick secure payment via Stripe before starting your blueprint.</p>
                </div>
              </div>
            )}

            {/* Company Info */}
            <div className="bg-slate-50 border border-slate-200 p-4 mb-8">
              <div className="flex items-center gap-4 text-xs font-['IBM_Plex_Sans'] text-slate-500">
                <span><strong className="text-slate-700">{invite.name}</strong></span>
                {invite.company && <><span>·</span><span>{invite.company}</span></>}
                <span>·</span><span>{invite.email}</span>
              </div>
            </div>

            <Button onClick={() => setStep("password")} className="w-full bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-14 text-base font-['Outfit'] font-medium" data-testid="invite-accept-btn">
              Get Started <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </motion.div>
        )}

        {step === "password" && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md mx-auto">
            <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#0033CC] mb-4">Create Your Account</p>
            <h2 className="text-2xl font-['Outfit'] font-medium tracking-tight text-[#0F172A] mb-2">Set your password</h2>
            <p className="text-sm text-slate-500 font-['IBM_Plex_Sans'] mb-8">This will be your login for accessing your blueprint results and reports.</p>

            {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 mb-4">{error}</div>}

            <div className="bg-white border border-slate-200 p-6 mb-6">
              <div className="mb-4">
                <Label className="text-sm font-medium text-slate-700">Email</Label>
                <Input value={invite.email} disabled className="mt-1.5 rounded-none border-slate-300 bg-slate-50" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">Create Password</Label>
                <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min. 6 characters" minLength={6} className="mt-1.5 rounded-none border-slate-300" data-testid="invite-password" />
              </div>
            </div>

            <Button onClick={handleAccept} disabled={accepting || password.length < 6} className="w-full bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-12 font-['Outfit'] font-medium" data-testid="invite-create-account-btn">
              {accepting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{invite.payment_required ? "Continue to Payment" : "Begin Your Blueprint"} <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
